import {
  resolveFindingType,
  resolveFindingSeverity,
  resolveFindingTitle,
  resolveFindingDescription,
} from "@/lib/finding-resolution";
import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import type {
  FindingType,
  Severity,
  AnalysisLens,
} from "@/components/finding-badges";
import type { DeltaItem } from "@/components/walkthrough/since-last-run";
import type { WalkthroughFinding, TriageStatus } from "./workspace-types";

interface ResolveFallbackParams {
  remoteFinding: any;
  walkthrough: any;
  selectedDeltaItem: DeltaItem | null;
  selectedFindingId: string | null;
}

export function buildFallbackRemoteFinding({
  remoteFinding,
  walkthrough,
  selectedDeltaItem,
  selectedFindingId,
}: ResolveFallbackParams): WalkthroughFinding | null {
  if (remoteFinding) {
    if ("isCanonical" in remoteFinding && remoteFinding.isCanonical) {
      const obs =
        (
          remoteFinding as {
            observations?: Array<{
              walkthroughId: string;
              walkthroughStepId?: string | null;
              targetBox?: TargetBox | null;
              stepNumber?: number | null;
            }>;
          }
        ).observations ?? [];

      const match = walkthrough?.id
        ? obs.find((o) => o.walkthroughId === walkthrough.id)
        : null;

      const walkthroughStepId =
        match?.walkthroughStepId ?? obs[0]?.walkthroughStepId ?? null;

      const targetBox =
        match?.targetBox ??
        obs.find((o) => o.targetBox)?.targetBox ??
        (remoteFinding as { effectiveTargetBox?: TargetBox | null })
          .effectiveTargetBox ??
        null;

      const stepNumber =
        (match && typeof match.stepNumber === "number"
          ? match.stepNumber
          : null) ??
        obs.find((o) => typeof o.stepNumber === "number")?.stepNumber ??
        (remoteFinding as { effectiveStepNumber?: number | null })
          .effectiveStepNumber ??
        null;

      const walkthroughId =
        (match && typeof match.stepNumber === "number"
          ? match.walkthroughId
          : null) ??
        obs[0]?.walkthroughId ??
        (remoteFinding as { effectiveWalkthroughId?: string | null })
          .effectiveWalkthroughId ??
        walkthrough?.id ??
        "";

      return {
        id: remoteFinding.id,
        type: resolveFindingType(
          remoteFinding,
          "hinders",
        ) as unknown as FindingType,
        severity: resolveFindingSeverity(remoteFinding),
        triageStatus: (remoteFinding.status === "dismissed"
          ? "dismissed"
          : remoteFinding.status === "addressed"
            ? "addressed"
            : "open") as TriageStatus,
        fixStatus: null,
        lens: null,
        title: resolveFindingTitle(remoteFinding) ?? null,
        description:
          resolveFindingDescription(remoteFinding) ??
          selectedDeltaItem?.title ??
          "",
        canonicalFindingId: remoteFinding.id,
        walkthroughStepId,
        targetBox,
        stepNumber,
        steps:
          (remoteFinding as { effectiveSteps?: number[] | null })
            .effectiveSteps ?? null,
        walkthroughId,
        uiFact:
          (remoteFinding as { effectiveUiFact?: string | null })
            .effectiveUiFact ?? null,
        consequence:
          (remoteFinding as { effectiveConsequence?: string | null })
            .effectiveConsequence ?? null,
        what:
          (remoteFinding as { effectiveWhat?: string | null }).effectiveWhat ??
          null,
        why:
          (remoteFinding as { effectiveWhy?: string | null }).effectiveWhy ??
          null,
        basis:
          (remoteFinding as { effectiveBasis?: string | null })
            .effectiveBasis ?? null,
        screen:
          (remoteFinding as { effectiveScreen?: string | null })
            .effectiveScreen ?? null,
        breaksRule:
          (remoteFinding as { effectiveBreaksRule?: string | null })
            .effectiveBreaksRule ?? null,
        breaksFile:
          (remoteFinding as { effectiveBreaksFile?: string | null })
            .effectiveBreaksFile ?? null,
      };
    }

    return {
      id: remoteFinding.id,
      type: (remoteFinding.type ?? "problem") as FindingType,
      severity: (remoteFinding.severity ?? null) as Severity | null,
      triageStatus: (remoteFinding.triageStatus ?? "open") as TriageStatus,
      fixStatus: remoteFinding.fixStatus ?? null,
      lens: (remoteFinding.lens ?? null) as AnalysisLens | null,
      title: remoteFinding.title ?? null,
      description: remoteFinding.description ?? selectedDeltaItem?.title ?? "",
      canonicalFindingId: remoteFinding.canonicalFindingId ?? null,
      walkthroughStepId: remoteFinding.walkthroughStepId ?? null,
      targetBox: (remoteFinding.targetBox as TargetBox) ?? null,
      uiFact: remoteFinding.uiFact ?? null,
      consequence: remoteFinding.consequence ?? null,
      what: remoteFinding.what ?? null,
      why: remoteFinding.why ?? null,
      basis: remoteFinding.basis ?? null,
      screen: remoteFinding.screen ?? null,
      steps: remoteFinding.steps ?? null,
      breaksRule: remoteFinding.breaksRule ?? null,
      breaksFile: remoteFinding.breaksFile ?? null,
      recommendation: remoteFinding.recommendation ?? null,
      stepNumber:
        (remoteFinding as { stepNumber?: number | null }).stepNumber ??
        remoteFinding.steps?.[0] ??
        null,
      walkthroughId:
        (remoteFinding as { walkthroughId?: string | null }).walkthroughId ??
        walkthrough?.id ??
        "",
    };
  }

  if (selectedDeltaItem) {
    return {
      id: selectedFindingId ?? "delta-item",
      type: (selectedDeltaItem.type ?? "problem") as FindingType,
      severity: null,
      triageStatus: "open",
      fixStatus: null,
      lens: null,
      title: selectedDeltaItem.title,
      description: selectedDeltaItem.title,
      canonicalFindingId: selectedDeltaItem.canonical_id ?? null,
      walkthroughStepId: null,
      targetBox: null,
    };
  }

  return null;
}
